import type { AiFieldSourceEntry } from "@/components/AiShiftProvenancePanel";
import {
  type AcceptCriterion,
  buildAcceptCriteriaFromPrefill,
  dedupeAcceptCriteria,
  eligibilityRowKey,
  type AcceptCriteriaSuggestionGroups,
} from "./acceptCriteriaUtils";

type MenuOption = { value: string; label: string };

export const AI_REVIEW_FOCUS_FIELDS = ["specialty", "minimum_skill_level"] as const;

const FIELD_LABELS: Record<string, string> = {
  specialty: "Specialty",
  minimum_skill_level: "Skill level",
};

export type AiMappedFieldAlert = {
  fieldKey: string;
  label: string;
  raw?: string;
  resolvedLabel: string;
  source: "inferred" | "fallback";
  alternatives?: string[];
};

function optionLabel(options: MenuOption[], id: string): string {
  if (!id) return "";
  const match = options.find((row) => row.value === id);
  return match?.label || id;
}

export function getAiMappedFieldAlerts(args: {
  fieldSources?: Record<string, AiFieldSourceEntry> | null;
  specialtyOptions: MenuOption[];
  skillOptions: MenuOption[];
  specialtyId: string;
  skillLevelId: string;
}): AiMappedFieldAlert[] {
  const { fieldSources, specialtyOptions, skillOptions, specialtyId, skillLevelId } =
    args;
  if (!fieldSources) return [];

  const resolvedByField: Record<string, string> = {
    specialty: optionLabel(specialtyOptions, specialtyId),
    minimum_skill_level: optionLabel(skillOptions, skillLevelId),
  };

  const alerts: AiMappedFieldAlert[] = [];

  for (const fieldKey of AI_REVIEW_FOCUS_FIELDS) {
    const entry = fieldSources[fieldKey];
    if (!entry) continue;
    if (entry.source !== "inferred" && entry.source !== "fallback") continue;

    const resolvedLabel = resolvedByField[fieldKey];
    if (!resolvedLabel) continue;

    alerts.push({
      fieldKey,
      label: FIELD_LABELS[fieldKey] || fieldKey,
      raw: entry.raw,
      resolvedLabel,
      source: entry.source,
      alternatives: entry.alternatives,
    });
  }

  return alerts;
}

export function suggestEmailEligibleRoles(args: {
  primarySpecialtyId: string;
  primarySkillId: string;
  specialtyAlternatives?: string[];
  skillLevelAlternatives?: string[];
  acceptCriteria: AcceptCriterion[];
  specialtyOptions: MenuOption[];
  skillOptions: MenuOption[];
}): AcceptCriterion[] {
  const built = buildAcceptCriteriaFromPrefill({
    primarySpecialtyId: args.primarySpecialtyId,
    primarySkillId: args.primarySkillId,
    specialtyAlternatives: args.specialtyAlternatives,
    skillLevelAlternatives: args.skillLevelAlternatives,
    specialtyOptions: args.specialtyOptions,
    skillOptions: args.skillOptions,
  });

  const existing = new Set(
    [
      eligibilityRowKey({
        specialtyId: args.primarySpecialtyId,
        minSkillLevelId: args.primarySkillId,
      }),
      ...args.acceptCriteria.map(eligibilityRowKey),
    ].filter(Boolean),
  );

  return built.filter((row) => !existing.has(eligibilityRowKey(row)));
}

export function mergeEligibleSuggestions(args: {
  emailSuggestions: AcceptCriterion[];
  suggestionGroups: AcceptCriteriaSuggestionGroups;
  acceptCriteria: AcceptCriterion[];
  primarySpecialtyId: string;
  primarySkillId: string;
  limit?: number;
}): AcceptCriterion[] {
  const existing = new Set(
    [
      eligibilityRowKey({
        specialtyId: args.primarySpecialtyId,
        minSkillLevelId: args.primarySkillId,
      }),
      ...args.acceptCriteria.map(eligibilityRowKey),
    ].filter(Boolean),
  );

  const merged: AcceptCriterion[] = [];
  const seen = new Set<string>();

  const tryPush = (row: AcceptCriterion) => {
    const key = eligibilityRowKey(row);
    if (!row.specialtyId || !row.minSkillLevelId) return;
    if (existing.has(key) || seen.has(key)) return;
    seen.add(key);
    merged.push(row);
  };

  for (const row of args.emailSuggestions) tryPush(row);
  for (const row of args.suggestionGroups.related) tryPush(row);
  for (const row of args.suggestionGroups.broad) tryPush(row);

  return merged.slice(0, args.limit ?? 8);
}

export function formatEligibleRoleLabel(
  row: AcceptCriterion,
  specialtyOptions: MenuOption[],
  skillOptions: MenuOption[],
): string {
  const spec = optionLabel(specialtyOptions, row.specialtyId);
  const skill = optionLabel(skillOptions, row.minSkillLevelId);
  return `${spec} · ${skill}`;
}

export function hasAiValidationReviewContent(args: {
  mappedAlerts: AiMappedFieldAlert[];
  eligibleSuggestions: AcceptCriterion[];
}): boolean {
  return args.mappedAlerts.length > 0 || args.eligibleSuggestions.length > 0;
}
